import { useCallback, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { PdfFieldRect, PdfFieldSample, PdfNormalizedRect } from "../types";

export interface UsePdfFieldManagerOptions {
  initialFields?: PdfFieldRect[];
  initialSamples?: PdfFieldSample[] | Record<string, string>;
  idFactory?: () => string;
  defaultMetaFactory?: (args: { id: string; index: number; page: number }) => PdfFieldRect["meta"];
  onFieldsChange?: (fields: PdfFieldRect[]) => void;
  onSamplesChange?: (samples: Record<string, string>) => void;
}

export interface PdfFieldManager {
  fields: PdfFieldRect[];
  setFields: Dispatch<SetStateAction<PdfFieldRect[]>>;
  selectedId: string | null;
  selectedField: PdfFieldRect | null;
  selectField: (id: string | null) => void;
  addField: (page: number, rect: PdfNormalizedRect, meta?: PdfFieldRect["meta"]) => PdfFieldRect;
  updateField: (id: string, updater: (field: PdfFieldRect) => PdfFieldRect) => void;
  updateFieldMeta: (id: string, meta: Partial<PdfFieldRect["meta"]>) => void;
  updateFieldRect: (id: string, rect: Partial<PdfNormalizedRect>) => void;
  removeField: (id: string) => void;
  replaceFields: (fields: PdfFieldRect[]) => void;
  sampleValues: Record<string, string>;
  sampleList: PdfFieldSample[];
  setSampleValue: (id: string, value: string) => void;
  setSampleValues: (next: Record<string, string>) => void;
  reset: (fields?: PdfFieldRect[], samples?: Record<string, string>) => void;
}

const defaultIdFactory = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `field_${Date.now()}_${Math.floor(Math.random() * 1_000_000)}`;
};

const defaultMetaFactory: Required<UsePdfFieldManagerOptions>["defaultMetaFactory"] = ({ index }) => ({
  label: `Field ${index + 1}`,
});

function toSampleRecord(samples?: PdfFieldSample[] | Record<string, string>) {
  if (!samples) {
    return {} as Record<string, string>;
  }

  if (Array.isArray(samples)) {
    return samples.reduce<Record<string, string>>((acc, sample) => {
      if (sample.id) {
        acc[sample.id] = sample.value ?? "";
      }
      return acc;
    }, {});
  }

  return { ...samples };
}

export function usePdfFieldManager({
  initialFields = [],
  initialSamples,
  idFactory = defaultIdFactory,
  defaultMetaFactory: createDefaultMeta = defaultMetaFactory,
  onFieldsChange,
  onSamplesChange,
}: UsePdfFieldManagerOptions = {}): PdfFieldManager {
  const [fields, setFields] = useState<PdfFieldRect[]>(initialFields);
  const [selectedId, setSelectedId] = useState<string | null>(initialFields[0]?.id ?? null);
  const [sampleValues, setSampleValuesState] = useState<Record<string, string>>(() =>
    toSampleRecord(initialSamples),
  );

  const selectedField = useMemo(
    () => fields.find((field) => field.id === selectedId) ?? null,
    [fields, selectedId],
  );

  const emitFieldsChange = useCallback(
    (nextFields: PdfFieldRect[]) => {
      setFields(nextFields);
      onFieldsChange?.(nextFields);
    },
    [onFieldsChange],
  );

  const emitSampleChange = useCallback(
    (nextSamples: Record<string, string>) => {
      setSampleValuesState(nextSamples);
      onSamplesChange?.(nextSamples);
    },
    [onSamplesChange],
  );

  const selectField = useCallback((id: string | null) => {
    setSelectedId(id);
  }, []);

  const addField = useCallback(
    (page: number, rect: PdfNormalizedRect, meta?: PdfFieldRect["meta"]) => {
      const id = idFactory();
      const nextField: PdfFieldRect = {
        id,
        page,
        rect,
        meta: meta ?? createDefaultMeta({ id, index: fields.length, page }),
      };

      const nextFields = [...fields, nextField];
      emitFieldsChange(nextFields);
      setSelectedId(id);
      emitSampleChange({ ...sampleValues, [id]: "" });
      return nextField;
    },
    [createDefaultMeta, emitFieldsChange, emitSampleChange, fields, idFactory, sampleValues],
  );

  const renameSampleKey = useCallback(
    (currentId: string, nextId: string) => {
      if (currentId === nextId) {
        return sampleValues;
      }
      const { [currentId]: currentValue = "", ...rest } = sampleValues;
      const nextSamples = { ...rest, [nextId]: currentValue };
      emitSampleChange(nextSamples);
      setSelectedId((prev) => {
        if (prev === currentId) {
          return nextId;
        }
        return prev;
      });
      return nextSamples;
    },
    [emitSampleChange, sampleValues],
  );

  const updateField = useCallback(
    (id: string, updater: (field: PdfFieldRect) => PdfFieldRect) => {
      let changed = false;
      let nextId = id;
      const nextFields = fields.map((field) => {
        if (field.id !== id) {
          return field;
        }
        const updated = updater(field);
        changed = changed || updated !== field;
        nextId = updated.id;
        return updated;
      });

      if (changed) {
        emitFieldsChange(nextFields);
        if (nextId !== id) {
          renameSampleKey(id, nextId);
        }
      }
    },
    [emitFieldsChange, fields, renameSampleKey],
  );

  const updateFieldMeta = useCallback(
    (id: string, meta: Partial<PdfFieldRect["meta"]>) => {
      updateField(id, (field) => ({
        ...field,
        meta: {
          ...(field.meta ?? {}),
          ...meta,
        },
      }));
    },
    [updateField],
  );

  const updateFieldRect = useCallback(
    (id: string, rect: Partial<PdfNormalizedRect>) => {
      updateField(id, (field) => ({
        ...field,
        rect: {
          ...field.rect,
          ...rect,
        },
      }));
    },
    [updateField],
  );

  const removeField = useCallback(
    (id: string) => {
      const nextFields = fields.filter((field) => field.id !== id);
      if (nextFields.length === fields.length) {
        return;
      }
      emitFieldsChange(nextFields);

      const { [id]: _removed, ...rest } = sampleValues;
      emitSampleChange(rest);

      setSelectedId((prev) => {
        if (prev === id) {
          return nextFields[0]?.id ?? null;
        }
        return prev;
      });
    },
    [emitFieldsChange, emitSampleChange, fields, sampleValues],
  );

  const replaceFields = useCallback(
    (nextFields: PdfFieldRect[]) => {
      emitFieldsChange(nextFields);
      setSelectedId(nextFields[0]?.id ?? null);

      const nextSamples = nextFields.reduce<Record<string, string>>((acc, field) => {
        const metaValue = (field.meta as Record<string, unknown> | undefined)?.value;
        if (typeof metaValue === "string") {
          acc[field.id] = metaValue;
        } else {
          const existing = sampleValues[field.id];
          acc[field.id] = existing ?? "";
        }
        return acc;
      }, {});
      emitSampleChange({ ...nextSamples });
    },
    [emitFieldsChange, emitSampleChange, sampleValues],
  );

  const setSampleValue = useCallback(
    (id: string, value: string) => {
      if (!id) {
        return;
      }
      emitSampleChange({ ...sampleValues, [id]: value });
    },
    [emitSampleChange, sampleValues],
  );

  const setSampleValues = useCallback(
    (next: Record<string, string>) => {
      emitSampleChange({ ...next });
    },
    [emitSampleChange],
  );

  const reset = useCallback(
    (nextFields = initialFields, nextSamplesRecord?: Record<string, string>) => {
      emitFieldsChange(nextFields);
      setSelectedId(nextFields[0]?.id ?? null);
      if (nextSamplesRecord) {
        emitSampleChange(nextSamplesRecord);
        return;
      }
      const defaultSamples = nextFields.reduce<Record<string, string>>((acc, field) => {
        const metaValue = (field.meta as Record<string, unknown> | undefined)?.value;
        if (typeof metaValue === "string") {
          acc[field.id] = metaValue;
          return acc;
        }
        acc[field.id] = sampleValues[field.id] ?? "";
        return acc;
      }, {});
      emitSampleChange(defaultSamples);
    },
    [emitFieldsChange, emitSampleChange, initialFields, sampleValues],
  );

  const sampleList = useMemo<PdfFieldSample[]>(() => {
    return Object.entries(sampleValues).map(([id, value]) => ({
      id,
      value,
    }));
  }, [sampleValues]);

  return {
    fields,
    setFields,
    selectedId,
    selectedField,
    selectField,
    addField,
    updateField,
    updateFieldMeta,
    updateFieldRect,
    removeField,
    replaceFields,
    sampleValues,
    sampleList,
    setSampleValue,
    setSampleValues,
    reset,
  };
}


